import { Button, CircularProgress,  } from '@mui/material';
import React, { MouseEventHandler, useMemo } from 'react';
import { COLORS } from '@styles';

const customButtonStyle = {
  height: `3rem`,
  display: `flex`,
  justifyContent: `center`,
  alignItems: `center`,
  borderRadius: `0.5rem`,
  fontFamily: 'Inter',
  fontWeight: 600,
  fontSize: '14px',

  '&.default-variant': {
    backgroundColor: COLORS.ELEKTRAFI_NEUTRAL,
    color: COLORS.NEUTRAL_WHITE,
    '&.Mui-disabled': {
      backgroundColor: COLORS.NEUTRAL_DARK_1,
      color: COLORS.NEUTRAL_LIGHT_0,
    },
  },

  '&.transparent-variant': {
    backgroundColor: `transparent`,
    borderColor: COLORS.NEUTRAL_WHITE,
    color: COLORS.NEUTRAL_WHITE,
  },

  '&.destructive-variant': {
    backgroundColor: COLORS.NEGATIVE_NEUTRAL,
    color: COLORS.NEUTRAL_WHITE,
    '&.Mui-disabled': {
      backgroundColor: COLORS.NEUTRAL_DARK_1,
      color: COLORS.NEUTRAL_LIGHT_0,
    },
  },

  '&.secondary-variant': {
    backgroundColor: COLORS.NEUTRAL_WHITE,
    color: COLORS.NEUTRAL_100,
    boxShadow: `none`,
    border: 'none'
  },
};

const iconContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
}

export type TCustomButtonVariant = `default` | `transparent` | `destructive` | `secondary`;

interface ICustomButtonProps {
  variant: TCustomButtonVariant;
  children?: any;
  onClick?: MouseEventHandler<HTMLButtonElement> | (() => void);
  id?: string;
  isLoading?: boolean;
  type?: `submit` | `reset` | `button`;
  disabled?: boolean;
  width?: string;
  icon?: React.ReactNode;
}

const getCustomButtonProps = (variant: TCustomButtonVariant) => {
  switch (variant) {
    case `transparent`:
      return `outlined`;
    case `destructive`:
      return `contained`;
    default:
      return `contained`;
  }
};

export function CustomButton({
  variant,
  onClick,
  id,
  children,
  isLoading,
  type,
  disabled,
  width,
  icon,
}: ICustomButtonProps) {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isLoading || typeof onClick !== `function`) {
      return;
    }
    onClick(event);
  };

  const muiVariant = useMemo(() => getCustomButtonProps(variant), [variant]);

  return (
    <Button
      variant={muiVariant}
      onClick={handleClick}
      id={id}
      type={type}
      disabled={disabled}
      sx = {{ ...customButtonStyle, width}}
      className={`${variant}-variant`}
    >
      {isLoading
        ? <CircularProgress size={25} color="inherit" />
        : <div style={iconContainerStyle}>{icon && icon}{children}</div>
      }
    </Button>
  )
}

CustomButton.defaultProps = {
  children: null,
  isLoading: false,
  type: `button`,
  disabled: false,
  width: `100%`,
  id: "",
  onClick: () => {},
};